@file:Suppress(
    "RETURN_VALUE_NOT_USED",
    "RETURN_VALUE_NOT_USED_COERCION",
)

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.browser.http.http
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Suppress
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.dom.hasClass
import kotlinx.dom.removeClass
import org.jetbrains.compose.web.renderComposable
import org.w3c.dom.EventSource
import org.w3c.dom.EventSourceInit
import org.w3c.dom.MessageEvent
import org.w3c.dom.`get`
import xyz.malefic.guptare.client.components.layouts.AdminLayout as _xyz_malefic_guptare_client_components_layouts_AdminLayout
import xyz.malefic.guptare.client.components.layouts.TopLevelLayout as _xyz_malefic_guptare_client_components_layouts_TopLevelLayout
import xyz.malefic.guptare.client.pages.ContactPage as _xyz_malefic_guptare_client_pages_ContactPage
import xyz.malefic.guptare.client.pages.HomePage as _xyz_malefic_guptare_client_pages_HomePage
import xyz.malefic.guptare.client.pages.TestimonialsPage as _xyz_malefic_guptare_client_pages_TestimonialsPage
import xyz.malefic.guptare.client.pages.WebinarPage as _xyz_malefic_guptare_client_pages_WebinarPage
import xyz.malefic.guptare.client.pages.admin.BlogPage as _xyz_malefic_guptare_client_pages_admin_BlogPage
import xyz.malefic.guptare.client.pages.admin.HomePage as _xyz_malefic_guptare_client_pages_admin_HomePage
import xyz.malefic.guptare.client.pages.admin.SiteSettingsPage as _xyz_malefic_guptare_client_pages_admin_SiteSettingsPage
import xyz.malefic.guptare.client.pages.admin.TestimonialsPage as _xyz_malefic_guptare_client_pages_admin_TestimonialsPage
import xyz.malefic.guptare.client.pages.admin.WebinarPage as _xyz_malefic_guptare_client_pages_admin_WebinarPage
import xyz.malefic.guptare.client.pages.blog.BlogIndexPage as _xyz_malefic_guptare_client_pages_blog_BlogIndexPage
import xyz.malefic.guptare.client.pages.blog.IndividualPostPage as _xyz_malefic_guptare_client_pages_blog_IndividualPostPage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

private fun forceReloadNow() {
    window.stop()
    window.location.reload()
}

private fun handleServerStatusEvents() {
    val status = document.getElementById("status")!!
    var lastVersion: Int? = null
    var shouldReload = false

    val warningIcon = status.children[0]!!
    val spinnerIcon = status.children[1]!!
    val statusText = status.children[2]!!

    status.addEventListener("transitionend", {
        if (status.hasClass("fade-out")) {
            status.removeClass("fade-out")
            if (shouldReload) {
                forceReloadNow()
            }
        }
    })

    val eventSource = EventSource("/api/kobweb-status", EventSourceInit(true))
    eventSource.addEventListener("version", { evt ->
        val version = (evt as MessageEvent).data.toString().toInt()
        if (lastVersion == null) {
            lastVersion = version
        }
        if (lastVersion != version) {
            lastVersion = version
            if (document.asDynamic().hidden) {
                // Reload immediately when the page is not visible as the animation will not run
                forceReloadNow()
            } else if (status.className.isNotEmpty()) {
                shouldReload = true
            } else {
                // Not sure if we can get here but if we can't rely on the status transition
                // to reload we should do it ourselves.
                forceReloadNow()
            }
        }
    })

    eventSource.addEventListener("status", { evt ->
        val values: dynamic = JSON.parse<Any>((evt as MessageEvent).data.toString())
        val text = values.text as String
        val isError = (values.isError as String).toBoolean()
        if (text.isNotBlank()) {
            warningIcon.className = if (isError) "visible" else "hidden"
            spinnerIcon.className = if (isError) "hidden" else "visible"
            statusText.innerHTML = "<i>$text</i>"
            status.className = "fade-in"
        } else {
            if (status.className == "fade-in") {
                status.className = "fade-out"
            }
        }
    })

    eventSource.onerror = { eventSource.close() }
}

public fun main() {
    handleServerStatusEvents()

    window.http.logOnError = true

    AppGlobals.initialize(mapOf("title" to "Gupta Real Estate"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "xyz.malefic.guptare.client.components.layouts.AdminLayout",
            initRouteMethod = { ctx ->
                xyz.malefic.guptare.client.components.layouts.initPageLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _xyz_malefic_guptare_client_components_layouts_AdminLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.registerLayout(
            "xyz.malefic.guptare.client.components.layouts.TopLevelLayout",
        ) { pageCtx, pageMethod -> 
            _xyz_malefic_guptare_client_components_layouts_TopLevelLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.register(
            "/",
            layoutId = "xyz.malefic.guptare.client.components.layouts.TopLevelLayout",
        ) { pageCtx -> 
            _xyz_malefic_guptare_client_pages_HomePage()
        }
        ctx.router.register(
            "/admin/",
            layoutId = "xyz.malefic.guptare.client.components.layouts.AdminLayout",
            initRouteMethod = { ctx ->
                xyz.malefic.guptare.client.pages.admin.initIndexPage(ctx)
            },
        ) { pageCtx -> 
            currentLayoutScope<xyz.malefic.guptare.client.components.layouts.AdminLayoutScope>().apply {
                _xyz_malefic_guptare_client_pages_admin_HomePage()
            }
        }
        ctx.router.register(
            "/admin/blog",
            layoutId = "xyz.malefic.guptare.client.components.layouts.AdminLayout",
            initRouteMethod = { ctx ->
                xyz.malefic.guptare.client.pages.admin.initBlogPage(ctx)
            },
        ) { pageCtx -> 
            currentLayoutScope<xyz.malefic.guptare.client.components.layouts.AdminLayoutScope>().apply {
                _xyz_malefic_guptare_client_pages_admin_BlogPage()
            }
        }
        ctx.router.register(
            "/admin/site",
            layoutId = "xyz.malefic.guptare.client.components.layouts.AdminLayout",
            initRouteMethod = { ctx ->
                xyz.malefic.guptare.client.pages.admin.initSitePage(ctx)
            },
        ) { pageCtx -> 
            currentLayoutScope<xyz.malefic.guptare.client.components.layouts.AdminLayoutScope>().apply {
                _xyz_malefic_guptare_client_pages_admin_SiteSettingsPage()
            }
        }
        ctx.router.register(
            "/admin/testimonials",
            layoutId = "xyz.malefic.guptare.client.components.layouts.AdminLayout",
            initRouteMethod = { ctx ->
                xyz.malefic.guptare.client.pages.admin.initTestimonialsPage(ctx)
            },
        ) { pageCtx -> 
            currentLayoutScope<xyz.malefic.guptare.client.components.layouts.AdminLayoutScope>().apply {
                _xyz_malefic_guptare_client_pages_admin_TestimonialsPage()
            }
        }
        ctx.router.register(
            "/admin/webinar",
            layoutId = "xyz.malefic.guptare.client.components.layouts.AdminLayout",
            initRouteMethod = { ctx ->
                xyz.malefic.guptare.client.pages.admin.initWebinarPage(ctx)
            },
        ) { pageCtx -> 
            currentLayoutScope<xyz.malefic.guptare.client.components.layouts.AdminLayoutScope>().apply {
                _xyz_malefic_guptare_client_pages_admin_WebinarPage()
            }
        }
        ctx.router.register(
            "/blog/",
            layoutId = "xyz.malefic.guptare.client.components.layouts.TopLevelLayout",
        ) { pageCtx -> 
            _xyz_malefic_guptare_client_pages_blog_BlogIndexPage()
        }
        ctx.router.register(
            "/blog/{id}",
            layoutId = "xyz.malefic.guptare.client.components.layouts.TopLevelLayout",
        ) { pageCtx -> 
            _xyz_malefic_guptare_client_pages_blog_IndividualPostPage(pageCtx)
        }
        ctx.router.register(
            "/contact",
            layoutId = "xyz.malefic.guptare.client.components.layouts.TopLevelLayout",
        ) { pageCtx -> 
            _xyz_malefic_guptare_client_pages_ContactPage()
        }
        ctx.router.register(
            "/testimonials",
            layoutId = "xyz.malefic.guptare.client.components.layouts.TopLevelLayout",
        ) { pageCtx -> 
            _xyz_malefic_guptare_client_pages_TestimonialsPage()
        }
        ctx.router.register(
            "/webinar",
            layoutId = "xyz.malefic.guptare.client.components.layouts.TopLevelLayout",
        ) { pageCtx -> 
            _xyz_malefic_guptare_client_pages_WebinarPage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("markdown-content", xyz.malefic.guptare.client.components.MarkdownContentStyle)
        ctx.theme.registerStyle("marquee-container", xyz.malefic.guptare.client.pages.MarqueeContainerStyle)
        ctx.theme.registerStyle("marquee-column", xyz.malefic.guptare.client.pages.MarqueeColumnStyle)
        ctx.theme.registerStyle("column1", xyz.malefic.guptare.client.pages.Column1Style)
        ctx.theme.registerStyle("column2", xyz.malefic.guptare.client.pages.Column2Style)
        ctx.theme.registerStyle("column3", xyz.malefic.guptare.client.pages.Column3Style)
        ctx.theme.registerStyle("display-lg", xyz.malefic.guptare.client.styles.DisplayLgStyle)
        ctx.theme.registerStyle("headline-md", xyz.malefic.guptare.client.styles.HeadlineMdStyle)
        ctx.theme.registerStyle("headline-sm", xyz.malefic.guptare.client.styles.HeadlineSmStyle)
        ctx.theme.registerStyle("body-lg", xyz.malefic.guptare.client.styles.BodyLgStyle)
        ctx.theme.registerStyle("body-md", xyz.malefic.guptare.client.styles.BodyMdStyle)
        ctx.theme.registerStyle("label-md", xyz.malefic.guptare.client.styles.LabelMdStyle)
        ctx.theme.registerStyle("label-sm", xyz.malefic.guptare.client.styles.LabelSmStyle)
        ctx.theme.registerStyle("input", xyz.malefic.guptare.client.styles.InputStyle)
        ctx.theme.registerStyle("search-input", xyz.malefic.guptare.client.styles.SearchInputStyle)
        ctx.theme.registerStyle("input-label", xyz.malefic.guptare.client.styles.InputLabelStyle)
        ctx.theme.registerStyle("input-helper", xyz.malefic.guptare.client.styles.InputHelperStyle)
        ctx.theme.registerStyle("input-error", xyz.malefic.guptare.client.styles.InputErrorStyle)
        ctx.theme.registerStyle("show-on-md", xyz.malefic.guptare.client.styles.ShowOnMdStyle)
        ctx.theme.registerStyle("hide-on-md", xyz.malefic.guptare.client.styles.HideOnMdStyle)
        ctx.theme.registerStyle("divider", xyz.malefic.guptare.client.styles.DividerStyle)
        ctx.theme.registerStyle("blob-accent", xyz.malefic.guptare.client.styles.BlobAccentStyle)
        ctx.theme.registerStyle("status-chip-accent", xyz.malefic.guptare.client.styles.StatusChipAccentStyle)
        ctx.theme.registerStyle("status-chip-neutral", xyz.malefic.guptare.client.styles.StatusChipNeutralStyle)
        ctx.theme.registerStyle("status-chip-tertiary", xyz.malefic.guptare.client.styles.StatusChipTertiaryStyle)
        ctx.theme.registerStyle("status-chip-error", xyz.malefic.guptare.client.styles.StatusChipErrorStyle)
        ctx.theme.registerStyle("floating-contact", xyz.malefic.guptare.client.styles.FloatingContactStyle)
        ctx.theme.registerStyle("section", xyz.malefic.guptare.client.styles.SectionStyle)
        ctx.theme.registerStyle("container", xyz.malefic.guptare.client.styles.ContainerStyle)
        ctx.theme.registerStyle("section-lavender", xyz.malefic.guptare.client.styles.SectionLavenderStyle)
        ctx.theme.registerStyle("section-warm", xyz.malefic.guptare.client.styles.SectionWarmStyle)
        ctx.theme.registerStyle("section-dim", xyz.malefic.guptare.client.styles.SectionDimStyle)
        ctx.theme.registerStyle("section-inverse", xyz.malefic.guptare.client.styles.SectionInverseStyle)
        ctx.theme.registerStyle("nav-bar", xyz.malefic.guptare.client.styles.NavBarStyle)
        ctx.theme.registerStyle("nav-link", xyz.malefic.guptare.client.styles.NavLinkStyle)
        ctx.theme.registerStyle("nav-link-active", xyz.malefic.guptare.client.styles.NavLinkActiveStyle)
        ctx.theme.registerStyle("primary-button", xyz.malefic.guptare.client.styles.PrimaryButtonStyle)
        ctx.theme.registerStyle("secondary-button", xyz.malefic.guptare.client.styles.SecondaryButtonStyle)
        ctx.theme.registerStyle("tertiary-button", xyz.malefic.guptare.client.styles.TertiaryButtonStyle)
        ctx.theme.registerStyle("property-card", xyz.malefic.guptare.client.styles.PropertyCardStyle)
        ctx.theme.registerStyle("property-image", xyz.malefic.guptare.client.styles.PropertyImageStyle)
        ctx.theme.registerStyle("card-body", xyz.malefic.guptare.client.styles.CardBodyStyle)
        ctx.theme.registerStyle("content-card", xyz.malefic.guptare.client.styles.ContentCardStyle)
        ctx.theme.registerStyle("glass-card", xyz.malefic.guptare.client.styles.GlassCardStyle)
        ctx.theme.registerKeyframes("spin", xyz.malefic.guptare.client.components.SpinKeyframes)
        xyz.malefic.guptare.client.styles.initSerenHearth(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        xyz.malefic.guptare.client.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
